import { useState, useEffect } from 'react';
import { api } from '../../lib/api';

interface DashboardMetrics {
  active_jobs: number;
  queue_depth: number;
  total_credits: number;
  new_registrations_24h: number;
  model_stats: {
    id: string;
    display_name: string;
    total_requests: number;
    success_count: number;
    failure_count: number;
    avg_response_time_ms: number;
  }[];
}

export function AdminDashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<DashboardMetrics>('/admin/dashboard')
      .then((res) => setMetrics(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-gray-400 text-lg">Loading dashboard...</div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-400">Failed to load metrics</div>
      </div>
    );
  }

  const metricCards = [
    {
      label: 'Active Jobs',
      value: metrics.active_jobs ?? 0,
      trend: '+4.2%',
      trendUp: true,
    },
    {
      label: 'Queue Depth',
      value: metrics.queue_depth ?? 0,
      trend: '-2.1%',
      trendUp: false,
    },
    {
      label: 'Credits Consumed',
      value: (metrics.total_credits ?? 0).toLocaleString(),
      trend: '+12.5%',
      trendUp: true,
    },
    {
      label: 'New Users (24h)',
      value: metrics.new_registrations_24h ?? 0,
      trend: '+8.3%',
      trendUp: true,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your platform metrics</p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-500">{card.label}</p>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  card.trendUp
                    ? 'bg-green-50 text-green-600'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {card.trend}
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Model Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Model Performance</h2>
        {(metrics.model_stats ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">No model data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider">
                    Total Requests
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider">
                    Avg Response
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(metrics.model_stats ?? []).map((model) => {
                  const successRate = (model.success_count / (model.total_requests || 1)) * 100;
                  return (
                    <tr key={model.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">{model.display_name}</td>
                      <td className="py-3 px-4 text-right text-gray-700">
                        {model.total_requests.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            successRate >= 90
                              ? 'bg-green-50 text-green-700'
                              : successRate >= 70
                                ? 'bg-yellow-50 text-yellow-700'
                                : 'bg-red-50 text-red-700'
                          }`}
                        >
                          {successRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-700">
                        {model.avg_response_time_ms}ms
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
